import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import useSeo from "../hooks/useSeo";
import usePromo from "../hooks/usePromo";
import OrderSummary from "../components/cart/OrderSummary";
import EmptyState from "../components/common/EmptyState";
import { ShoppingBag } from "lucide-react";
import { WhatsAppIcon } from "../components/layout/FloatingWhatsApp";
import { useStore } from "../context/StoreProvider";
import { formatPrice } from "../lib/utils";
import { openWhatsAppOrder } from "../lib/whatsapp";
import { storeConfig } from "../config/store.config";

const FIELDS = [
  { name: "name", label: "Full Name", type: "text", placeholder: "Your name" },
  { name: "mobile", label: "Mobile Number", type: "tel", placeholder: "10-digit mobile" },
  { name: "address", label: "Address", type: "text", placeholder: "House no, street, area", full: true },
  { name: "city", label: "City", type: "text", placeholder: "City" },
  { name: "state", label: "State", type: "text", placeholder: "State" },
  { name: "pincode", label: "PIN Code", type: "tel", placeholder: "6-digit PIN" },
];

const Checkout = () => {
  const { cart, cartCount, subtotal } = useStore();
  const promo = usePromo(subtotal);
  const [form, setForm] = useState({
    name: "", mobile: "", address: "", city: "", state: "", pincode: "", note: "",
  });
  const [errors, setErrors] = useState({});

  useSeo({ title: "Checkout", description: "Complete your 9'One Store order via WhatsApp.", path: "/checkout" });

  if (cart.length === 0) {
    return (
      <div className="container-9one">
        <EmptyState
          icon={ShoppingBag}
          title="Nothing to order"
          message="Your cart is empty."
          ctaLabel="Shop Now"
          ctaTo="/shop"
          testId="checkout-empty"
        />
      </div>
    );
  }

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, ""))) e.mobile = "Enter a valid 10-digit mobile";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state.trim()) e.state = "State is required";
    if (!/^\d{6}$/.test(form.pincode.replace(/\D/g, ""))) e.pincode = "Enter a valid 6-digit PIN";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const placeOrder = () => {
    if (!validate()) {
      toast("Please complete the required fields");
      const firstErr = document.querySelector('[data-error="true"]');
      firstErr?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    openWhatsAppOrder({
      items: cart,
      customer: form,
      totals: {
        itemCount: cartCount,
        subtotal,
        discount: promo.discount,
        promoCode: promo.appliedPromo,
        total: promo.total,
      },
    });
    toast("Opening WhatsApp to place your order…");
  };

  return (
    <div className="container-9one py-10 animate-fade-in">
      <Link to="/cart" className="inline-flex items-center gap-2 label-mono text-ink/50 hover:text-ink" data-testid="back-to-cart">
        <ArrowLeft size={14} /> Back to Cart
      </Link>
      <h1 className="mt-4 font-display text-5xl uppercase tracking-tight sm:text-6xl">Checkout</h1>
      <p className="mt-2 max-w-xl text-sm text-ink/60">
        Enter your delivery details and review your order. Your order is placed
        via WhatsApp — no online payment. Your details are used only to build the
        order message and are not stored.
      </p>

      <div className="mt-10 grid gap-10 lg:grid-cols-3">
        {/* Form + review */}
        <div className="lg:col-span-2">
          <h2 className="mb-5 font-display text-3xl uppercase tracking-tight">Delivery Details</h2>
          <div className="grid grid-cols-2 gap-4" data-testid="order-form">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.full ? "col-span-2" : "col-span-2 sm:col-span-1"}>
                <label className="label-mono mb-1.5 block" htmlFor={f.name}>{f.label} *</label>
                <input
                  id={f.name}
                  type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`order-${f.name}`}
                  data-error={!!errors[f.name]}
                  className={`w-full border bg-transparent px-4 py-3 text-sm focus:outline-none ${
                    errors[f.name] ? "border-red-500" : "border-ink/25 focus:border-ink"
                  }`}
                />
                {errors[f.name] && (
                  <p className="mt-1 font-mono text-xs text-red-600" data-testid={`error-${f.name}`}>
                    {errors[f.name]}
                  </p>
                )}
              </div>
            ))}
            <div className="col-span-2">
              <label className="label-mono mb-1.5 block" htmlFor="note">Order Note (optional)</label>
              <textarea
                id="note"
                rows={3}
                value={form.note}
                onChange={(e) => setField("note", e.target.value)}
                placeholder="Anything we should know?"
                data-testid="order-note"
                className="w-full border border-ink/25 bg-transparent px-4 py-3 text-sm focus:border-ink focus:outline-none"
              />
            </div>
          </div>

          {/* Review items */}
          <h2 className="mb-4 mt-10 font-display text-3xl uppercase tracking-tight">Order Review</h2>
          <div className="divide-y divide-ink/10 border-y border-ink/10" data-testid="order-review">
            {cart.map((it) => (
              <div key={it.lineId} className="flex items-center gap-4 py-4">
                <div className="h-20 w-16 shrink-0 overflow-hidden bg-cream">
                  <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{it.name}</p>
                  <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink/50">
                    {it.size} · {it.color} · Qty {it.quantity}
                  </p>
                </div>
                <span className="font-mono text-sm">{formatPrice(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Summary + WhatsApp */}
        <div className="lg:col-span-1">
          <OrderSummary
            subtotal={subtotal}
            discount={promo.discount}
            total={promo.total}
            appliedPromo={promo.appliedPromo}
            promoInput={promo.promoInput}
            setPromoInput={promo.setPromoInput}
            onApplyPromo={promo.applyPromo}
            onRemovePromo={promo.removePromo}
            promoError={promo.promoError}
          >
            <button
              onClick={placeOrder}
              className="btn-base w-full bg-[#25D366] px-7 py-4 text-white hover:bg-[#1eb955]"
              data-testid="place-order-whatsapp-btn"
            >
              <WhatsAppIcon size={18} /> Place Order on WhatsApp
            </button>
            <p className="mt-3 text-center font-mono text-[11px] leading-relaxed text-ink/50">
              Order goes to {storeConfig.primaryContactName} · {storeConfig.primaryPhoneIntl}
            </p>
          </OrderSummary>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
